import { Analytics, DriverConfig } from '@easeltv/analytics';
import { AnalyticsConfig } from '@easeltv/client-sdk';
import UAParser from 'ua-parser-js';

/* -------------------------------- Constants ------------------------------- */

/**
 * This version should be bumped any time we change the /privacy page to add new services
 *
 * This will trigger users to have to re-confirm their decision
 */
export const CONSENT_VERSION = 1;

/* ---------------------------------- Types --------------------------------- */

/** Decision's a user can take */
export type ConsentDecision = 'all' | 'essential';

/** Shape of the decision object stored in local storage */
export interface Consent {
    version: number;
    decision: ConsentDecision | 'empty';
}

/* --------------------------------- Helpers -------------------------------- */

/**
 * initAnalytics
 *
 * initAnalytics
 *
 * @param analyticsConfig
 * @param sessionId
 * @param consentDecision
 */
export const initAnalytics = async (
    analyticsConfig: AnalyticsConfig,
    sessionId: string | undefined,
    userId: string | undefined,
    consentDecision: string,
    appName: string,
) => {
    if (window.analytics) return;

    const ua = new UAParser(navigator.userAgent);

    const analyticsDrivers: DriverConfig[] = [];

    // Log to console in local development
    if (import.meta.env.DEV || import.meta.env.VITE_API_ENV === 'dev')
        analyticsDrivers.push({
            type: 'console',
            namespace: 'DEV',
        });
    // If not in development add config drivers
    if (analyticsConfig?.google?.measurementId)
        analyticsDrivers.push({
            type: 'ga',
            measurementId: analyticsConfig.google.measurementId,
        });
    const bcConf = analyticsConfig?.brightcove;
    if (bcConf?.enabled && bcConf.accountId) {
        analyticsDrivers.push({
            type: 'brightcove',
            accountId: bcConf.accountId,
        });
    }

    window.analytics = Analytics({
        drivers: analyticsDrivers,
    });

    await window.analytics.initialize();

    window.analytics.setDefaultData({
        appName,
        appVersion: import.meta.env.VITE_VERSION_TAG,
        deviceHorizontalRes: `${window.screen.availWidth}`,
        deviceVerticalRes: `${window.screen.availHeight}`,
        sessionId: sessionId ?? '',
        userId: userId ?? '',
        devicePlatform: 'web',
        deviceManufacturer: ua.getDevice().vendor ?? 'unknown',
        deviceOs: ua.getOS().name ?? 'unknown',
        deviceType: ua.getDevice().model ?? 'unknown',
        osVersion: ua.getOS().version ?? 'unknown',
    });

    if (consentDecision === 'all') {
        window.analytics.enable();
    } else {
        window.analytics.disable();
    }

    window.analytics.trigger({
        type: 'app.loaded',
    });
};
